import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";
import { SearchTermsFilters, SearchTermsGridItem, SearchTermsGridResponse, OpportunityCardsResponse, ClicksAdCostsResponse, VisibilityResponse, TopCompetitorsResponse, RegionsResponse, CompetitorsResponse, CompetitorsGridResponse, ClientCompetitorsResponse } from "./models";
import { PaginateQuery, SearchTermsFiltersQuery, SearchTermsFiltersQueryWithSearchTerms } from "./query";
import { GridFilterModel } from "@mui/x-data-grid";

const nonBrandedAPI = createApi({
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL, credentials: 'include' }),
    reducerPath: 'api.nonBranded',
    tagTypes: ['nonBranded'],
    endpoints: (builder) => ({
        searchTermsFilters: builder.query<SearchTermsFilters, SearchTermsFiltersQuery>({
            query: (query) => ({
                url: '/non-branded/filters',
                method: 'GET',
                params: query,
            }),
        }),
        searchTermsGrid: builder.query<SearchTermsGridResponse, SearchTermsFiltersQueryWithSearchTerms & PaginateQuery & { filterModel?: GridFilterModel["items"], opportunity?: string | null, sort_field?: string, sort_order?: string, interval?: string }>({
            query: (query) => {
                let url = '/non-branded/search_terms/grid';
                let params = [];
                if (query.countries && query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices && query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms && query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                if (query.search_terms && query.search_terms.length > 0) {
                    params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
                }

                const limit = `&limit=${query.limit || 10}`
                const page = `&page=${query.page || 0}`
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                const filter_model = query.filterModel ? `&filter_model=${JSON.stringify(query.filterModel)}` : "";
                const opportunity = query.opportunity ? `&opportunity=${query.opportunity}` : "";
                const sort_field = query.sort_field ? `&sort_field=${query.sort_field}&sort_order=${query.sort_order}` : "";

                return ({
                    url: url + '?' + params.join('&') + limit + page + workspace_id + start_date + end_date + filter_model + opportunity + sort_field,
                    method: 'GET',
                })
            },
        }),
        opportunityCards: builder.query<OpportunityCardsResponse, SearchTermsFiltersQueryWithSearchTerms>({
            query: (query) => {
                let url = '/non-branded/search_terms/opportunity_cards';
                let params = [];
                if (query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                if (query.search_terms.length > 0) {
                    params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date,
                    method: 'GET',
                })
            },
        }),
        clicksAdCosts: builder.query<ClicksAdCostsResponse, SearchTermsFiltersQueryWithSearchTerms & { interval?: string }>({
            query: (query) => {
                let url = '/non-branded/search_terms/clicks_ad_costs';
                let params = [];
                if (query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                if (query.search_terms.length > 0) {
                    params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                const interval = query.interval ? `&interval=${query.interval}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval,
                    method: 'GET',
                })
            },
        }),
        visibility: builder.query<VisibilityResponse, SearchTermsFiltersQueryWithSearchTerms & { interval?: string }>({
            query: (query) => {
                let url = '/non-branded/search_terms/visibility';
                let params = [];
                if (query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                if (query.search_terms.length > 0) {
                    params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                const interval = query.interval ? `&interval=${query.interval}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval,
                    method: 'GET',
                })
            },
        }),
        topCompet: builder.query<TopCompetitorsResponse, SearchTermsFiltersQueryWithSearchTerms & { interval?: string }>({
            query: (query) => {
                let url = '/non-branded/search_terms/top_compet';
                let params = [];
                if (query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                const interval = query.interval ? `&interval=${query.interval}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval,
                    method: 'GET',
                })
            },
        }),
        regions: builder.query<RegionsResponse, SearchTermsFiltersQueryWithSearchTerms>({
            query: (query) => {
                let url = '/non-branded/search_terms/regions';
                let params = [];
                if (query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                if (query.search_terms.length > 0) {
                    params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date,
                    method: 'GET',
                })
            },
        }),
        keywordClicksAdCosts: builder.query<ClicksAdCostsResponse, SearchTermsFiltersQuery & { keyword: string, interval?: string }>({
            query: (query) => {
                let url = '/non-branded/search_terms/keyword/clicks_ad_costs';
                let params = [];
                if (query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                const keyword = query.keyword ? `&keyword=${encodeURIComponent(query.keyword)}` : "";
                const interval = `&interval=year_week`;
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval + keyword,
                    method: 'GET',
                })
            },
        }),
        keywordVisibility: builder.query<VisibilityResponse, SearchTermsFiltersQuery & { keyword: string, interval?: string }>({
            query: (query) => {
                let url = '/non-branded/search_terms/keyword/visibility';
                let params = [];
                if (query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                const interval = query.interval ? `&interval=${query.interval}` : "";
                const keyword = query.keyword ? `&keyword=${encodeURIComponent(query.keyword)}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval + keyword,
                    method: 'GET',
                })
            },
        }),
        keywordTopCompet: builder.query<TopCompetitorsResponse, SearchTermsFiltersQuery & { keyword: string, interval?: string }>({
            query: (query) => {
                let url = '/non-branded/search_terms/keyword/top_compet';
                let params = [];
                if (query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                const interval = query.interval ? `&interval=${query.interval}` : "";
                const keyword = query.keyword ? `&keyword=${encodeURIComponent(query.keyword)}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval + keyword,
                    method: 'GET',
                })
            },
        }),
        competitors: builder.query<CompetitorsResponse, SearchTermsFiltersQuery>({
            query: (query) => {
                let url = '/non-branded/competitors/competitors';
                let params = [];
                if (query.countries && query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices && query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms && query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date,
                    method: 'GET',
                })
            },
        }),
        clientCompetitors: builder.query<ClientCompetitorsResponse, SearchTermsFiltersQuery>({
            query: (query) => {
                let url = '/non-branded/competitors/client-competitors';
                let params = [];
                if (query.countries && query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices && query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date,
                    method: 'GET',
                })
            },
        }),  
        competitorsGrid: builder.query<CompetitorsGridResponse, SearchTermsFiltersQueryWithSearchTerms & { filterModel?: GridFilterModel["items"] }>({
            query: (query) => {
                let url = '/non-branded/competitors/competitors-grid';
                let params = [];
                if (query.countries && query.countries.length > 0) {
                    params.push(`countries=${query.countries.map(country => country.replace('&', '__et__')).join('&countries=')}`);
                }
                if (query.devices && query.devices.length > 0) {
                    params.push(`devices=${query.devices.join('&devices=')}`);
                }
                if (query.platforms && query.platforms.length > 0) {
                    params.push(`platforms=${query.platforms.join('&platforms=')}`);
                }
                if (query.search_terms && query.search_terms.length > 0) {
                    params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
                }
                const page = query.page ? `&page=${query.page}` : "";
                const page_size = query.page_size ? `&page_size=${query.page_size}` : "";
                const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : "";
                const start_date = query.start_date ? `&start_date=${query.start_date}` : "";
                const end_date = query.end_date ? `&end_date=${query.end_date}` : "";
                const filter_model = query.filterModel ? `&filter_model=${JSON.stringify(query.filterModel)}` : "";
                return ({
                    url: url + '?' + params.join('&') + workspace_id + start_date + end_date + page + page_size + filter_model,
                    method: 'GET',
                })
            },
        }),
    }),
});

export const {
    useSearchTermsFiltersQuery,
    useSearchTermsGridQuery,
    useOpportunityCardsQuery,
    useClicksAdCostsQuery,
    useVisibilityQuery,
    useTopCompetQuery,
    useRegionsQuery,
    useKeywordClicksAdCostsQuery,
    useKeywordVisibilityQuery,
    useKeywordTopCompetQuery,
    useCompetitorsQuery,
    useClientCompetitorsQuery,
    useCompetitorsGridQuery
} = nonBrandedAPI;
export default nonBrandedAPI;